import React from 'react';

// Domain Models
interface MetricValue {
  current: number;
  maximum: number;
}

interface ThresholdBounds {
  red: number;
  orange: number;
  green: number;
}

interface ColorZone {
  color: string;
  startPercentage: number;
  endPercentage: number;
}

enum MetricType {
  STRAIN = 'strain',
  RECOVERY = 'recovery'
}

// Domain Services
class ThresholdCalculator {
  static calculateZones(bounds: ThresholdBounds, max: number, type: MetricType): ColorZone[] {
    const isStrain = type === MetricType.STRAIN;
    
    if (isStrain) {
      const greenEnd = (bounds.green / max) * 100;
      const orangeEnd = ((bounds.green + bounds.orange) / max) * 100;
      
      return [
        { color: '#00C851', startPercentage: 0, endPercentage: greenEnd },
        { color: '#FF8800', startPercentage: greenEnd, endPercentage: orangeEnd },
        { color: '#FF1744', startPercentage: orangeEnd, endPercentage: 100 }
      ];
    } else {
      const redEnd = (bounds.red / max) * 100;
      const orangeEnd = ((bounds.red + bounds.orange) / max) * 100;
      
      return [
        { color: '#FF1744', startPercentage: 0, endPercentage: redEnd },
        { color: '#FF8800', startPercentage: redEnd, endPercentage: orangeEnd },
        { color: '#00C851', startPercentage: orangeEnd, endPercentage: 100 }
      ];
    }
  }
}

class StatusEvaluator {
  static determineStatus(percentage: number, zones: ColorZone[]): string {
    const zone = zones.find(z => percentage >= z.startPercentage && percentage <= z.endPercentage);
    return zone?.color || zones[zones.length - 1].color;
  }
}

// Main Component
interface ProgressInput {
  resultSet: number;
  max: number;
  green: number;
  orange: number;
  red: number;
}

const Progress = ({ resultSet, max, red, orange, green }: ProgressInput) => {
  // Domain object creation
  const metricValue: MetricValue = { current: resultSet, maximum: max };
  const thresholds: ThresholdBounds = { red, orange, green };
  const metricType = max === 20 ? MetricType.STRAIN : MetricType.RECOVERY;
  
  // Business logic
  const progressPercentage = Math.min((metricValue.current / metricValue.maximum) * 100, 100);
  const colorZones = ThresholdCalculator.calculateZones(thresholds, metricValue.maximum, metricType);
  const statusColor = StatusEvaluator.determineStatus(progressPercentage, colorZones);
  
  // Rendering constants (smaller size)
  const radius = 60; // Reduced from 85
  const circumference = 2 * Math.PI * radius;
  const arcLength = (220 / 360) * circumference;
  const progressLength = (progressPercentage / 100) * arcLength;
  
  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      minHeight: '100px', // Reduced from 140px
      padding: '0.5rem', // Reduced padding
    },
    innerContainer: {
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      maxWidth: '170px', // Reduced from 240px
      aspectRatio: '2 / 1.2',
    },
    svg: {
      width: '100%',
      height: '100%',
      maxWidth: '170px', // Reduced from 240px
      maxHeight: '100px', // Reduced from 140px
      overflow: 'visible',
    },
    statusIndicator: {
      position: 'absolute' as const,
      bottom: '-6px', // Adjusted for smaller size
      left: '50%',
      transform: 'translateX(-50%)',
      width: '6px', // Reduced from 8px
      height: '6px', // Reduced from 8px
      borderRadius: '50%',
      backgroundColor: statusColor,
      transition: 'background-color 0.3s ease, transform 0.2s ease',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
    }
  };
  
  const renderZoneIndicators = () => {
    return colorZones.map((zone, index) => {
      const zoneLength = ((zone.endPercentage - zone.startPercentage) / 100) * arcLength;
      const offset = (zone.startPercentage / 100) * arcLength;
      
      return (
        <path
          key={index}
          d="M 21 70 A 60 60 0 1 1 139 70" // Adjusted for smaller radius
          fill="none"
          stroke={zone.color}
          strokeWidth="6" // Reduced from 8
          strokeDasharray={`0 ${offset} ${zoneLength} ${arcLength}`}
          strokeLinecap="round"
          opacity="0.4" // Increased opacity for bolder appearance
        />
      );
    });
  };
  
  return (
    <div style={styles.container}>
      <div style={styles.innerContainer}>
        <svg style={styles.svg} viewBox="0 0 140 85"> {/* Adjusted viewBox for smaller size */}
          {/* Background track */}
          <path
            d="M 21 70 A 60 60 0 1 1 139 70" // Adjusted for smaller radius
            fill="none"
            stroke="#e0e0e0" // Slightly darker background for contrast
            strokeWidth="8" // Reduced from 12
            strokeLinecap="round"
          />
          
          {/* Zone indicators */}
          {renderZoneIndicators()}
          
          {/* Progress indicator */}
          <path
            d="M 21 70 A 60 60 0 1 1 139 70" // Adjusted for smaller radius
            fill="none"
            stroke={statusColor}
            strokeWidth="8" // Reduced from 12
            strokeDasharray={`${progressLength} ${arcLength}`}
            strokeLinecap="round"
            style={{
              filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))',
              transition: 'stroke-dasharray 0.8s ease-in-out, stroke 0.3s ease'
            }}
          />
          
          {/* Center value */}
          <text
            x="80" // Adjusted for smaller viewBox
            y="60" // Adjusted for smaller viewBox
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontFamily: 'Segoe UI, system-ui, sans-serif',
              fontSize: '20px', // Reduced from 28px
              fontWeight: '700',
              fontVariantNumeric: 'tabular-nums',
              fill: '#1a1a1a', // Darker for better contrast
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
            }}
          >
            {parseFloat(metricValue.current.toString()).toFixed(2)}
          </text>
          
          {/* Max value indicator */}
          <text
            x="80" // Adjusted for smaller viewBox
            y="75" // Adjusted for smaller viewBox
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontFamily: 'Segoe UI, system-ui, sans-serif',
              fontSize: '10px', // Reduced from 14px
              fontWeight: '500',
              fill: '#666666' // Slightly darker for better visibility
            }}
          >
            / {metricValue.maximum}
          </text>
        </svg>
      </div>
    </div>
  );
};

export default Progress;